import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { useToast } from './ToastContext';

const WeeklyPromptsContext = createContext(null);

export const useWeeklyPrompts = () => {
  const context = useContext(WeeklyPromptsContext);
  if (!context) {
    throw new Error('useWeeklyPrompts must be used within a WeeklyPromptsProvider');
  }
  return context;
};

export const WeeklyPromptsProvider = ({ children }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { addToast } = useToast();
  const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

  const processPrompts = (weeklyPrompts) => {
    if (!Array.isArray(weeklyPrompts)) {
      console.error('Invalid prompts data structure:', weeklyPrompts);
      return [];
    }

    // Sort weeks by date range (most recent first)
    return weeklyPrompts.sort((a, b) => {
      const dateA = new Date(a.dateRange.start);
      const dateB = new Date(b.dateRange.start);
      return dateB - dateA;
    });
  };

  const loadWeeklyPrompts = async (force = false) => {
    try {
      // Check if cache is still valid
      if (!force && lastUpdated && (Date.now() - lastUpdated < CACHE_DURATION)) {
        const cached = localStorage.getItem('weeklyPrompts');
        if (cached) {
          const { prompts: cachedPrompts } = JSON.parse(cached);
          if (cachedPrompts && Array.isArray(cachedPrompts)) {
            setPrompts(processPrompts(cachedPrompts));
            return;
          }
        }
      }

      setLoading(true);
      const response = await apiService.getWeeklyPrompts();
      
      if (!response || !response.weeklyPrompts) {
        throw new Error('Invalid response format');
      }

      const processedPrompts = processPrompts(response.weeklyPrompts);
      setPrompts(processedPrompts);
      setLastUpdated(Date.now());
      
      // Cache the prompts in localStorage as backup
      localStorage.setItem('weeklyPrompts', JSON.stringify({
        prompts: processedPrompts,
        timestamp: Date.now()
      }));

    } catch (error) {
      console.error('Error loading weekly prompts:', error);
      addToast('Failed to load weekly prompts. Please try again later.', 'error');
      
      // Try to load from localStorage if API fails
      const cached = localStorage.getItem('weeklyPrompts');
      if (cached) {
        try {
          const { prompts: cachedPrompts, timestamp } = JSON.parse(cached);
          if (cachedPrompts && Array.isArray(cachedPrompts)) {
            setPrompts(processPrompts(cachedPrompts));
            setLastUpdated(timestamp);
          }
        } catch (parseError) {
          console.error('Error parsing cached prompts:', parseError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    const initializePrompts = async () => {
      // Try to load from localStorage first for immediate display
      const cached = localStorage.getItem('weeklyPrompts');
      if (cached) {
        try {
          const { prompts: cachedPrompts, timestamp } = JSON.parse(cached);
          if (cachedPrompts && Array.isArray(cachedPrompts)) {
            setPrompts(processPrompts(cachedPrompts));
            setLastUpdated(timestamp);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error loading cached prompts:', error);
        }
      }
      
      // Then fetch fresh data
      await loadWeeklyPrompts();
    };

    initializePrompts();
  }, []);

  // Periodic check for updates
  useEffect(() => {
    const interval = setInterval(() => {
      loadWeeklyPrompts(true);
    }, CACHE_DURATION);

    return () => clearInterval(interval);
  }, []);

  const refreshPrompts = () => loadWeeklyPrompts(true);

  return (
    <WeeklyPromptsContext.Provider value={{
      prompts,
      loading,
      lastUpdated,
      refreshPrompts
    }}>
      {children}
    </WeeklyPromptsContext.Provider>
  );
};